import React, { useState } from "react";
import {
  Box,
  Paper,
  TextInput,
  Select,
  Button,
  Title,
  Text,
  Group,
  Stack,
  LoadingOverlay,
  Alert,
  Textarea,
  Grid,
  Divider,
  FileInput,
} from "@mantine/core";
import {
  IconBrandInstagram,
  IconUpload,
  IconTags,
  IconCategory,
  IconUserCheck,
  IconAlertTriangle,
  IconFileUpload,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";
import { uploadManualContent } from "../../utils/directUpload";

const ACCEPT_BY_TYPE = {
  Reels: "video/mp4",
  Feeds: "video/mp4,image/jpeg",
};

export default function ContentUploader({ defaultType = "Reels", onSuccess }) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    type: defaultType,
    category: "Anime",
    creatorTag: "",
    customCaption: "",
    file: null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file)
      return showError("Missing File", "A media file is required.");

    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const result = await uploadManualContent(API_BASE_URL, token, formData);

      if (result.success) {
        showSuccess("Published!", result.message);
        // Reset form like a CMS
        if (onSuccess) onSuccess();
        setFormData({
          type: "Reels",
          category: "Anime",
          creatorTag: "",
          customCaption: "",
          file: null,
        });
      } else {
        showError("Upload Failed", result.error);
      }
    } catch (error) {
      showError("Network Error", "Failed to connect to backend.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxW={800} mx="auto" mt="xl">
      <Paper withBorder shadow="sm" p="xl" radius="md" pos="relative">
        <LoadingOverlay
          visible={loading}
          overlayProps={{ blur: 2 }}
          loaderProps={{ type: "bars" }}
        />

        {loading && (
          <Box
            pos="absolute"
            top="60%"
            left={0}
            right={0}
            style={{ zIndex: 400 }}
            ta="center"
          >
            <Text fw={700} size="lg" c="blue">
              Uploading Media...
            </Text>
            <Text size="sm" c="dimmed">
              Processing → S3 Upload → Database Sync
            </Text>
          </Box>
        )}

        <Group mb="md">
          <IconBrandInstagram size={32} color="#E1306C" />
          <Box>
            <Title order={3}>Create Native Content</Title>
            <Text c="dimmed" size="sm">
              Direct CMS replacement for Strapi.
            </Text>
          </Box>
        </Group>

        <Divider mb="xl" />

        <Alert
          color="yellow"
          icon={<IconAlertTriangle size={18} />}
          title="Instagram link ingestion is temporarily disabled"
          mb="lg"
        >
          The automatic Instagram scraper/converter is currently broken
          (Instagram has blocked the unauthenticated scraping method it
          relied on). Please upload the media file directly below until this
          is resolved.
        </Alert>

        <form onSubmit={handleSubmit}>
          <Stack gap="lg">
            {/* DISABLED: link-based ingestion, kept visible for context */}
            <TextInput
              label="Instagram URL"
              description="Link-based ingestion is temporarily disabled — the converter system is currently facing issues. Use the file upload below instead."
              placeholder="https://www.instagram.com/reel/..."
              disabled
              size="md"
              leftSection={<IconBrandInstagram size={18} />}
            />

            {/* MANUAL FILE UPLOAD (temporary replacement) */}
            <FileInput
              label="Media File"
              description={
                formData.type === "Reels"
                  ? "Upload an MP4 video for this reel."
                  : "Upload an MP4 video or JPG image for this feed post."
              }
              placeholder="Choose file..."
              required
              size="md"
              accept={ACCEPT_BY_TYPE[formData.type]}
              value={formData.file}
              onChange={(file) => setFormData({ ...formData, file })}
              leftSection={<IconFileUpload size={18} />}
              clearable
            />

            {/* METADATA GRID (Replaces Strapi Sidebar) */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Select
                  label="Content Type"
                  required
                  data={[
                    { value: "Reels", label: "Video Reel" },
                    { value: "Feeds", label: "Image/Carousel Feed" },
                  ]}
                  value={formData.type}
                  onChange={(val) => setFormData({ ...formData, type: val })}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="Category"
                  placeholder="e.g., Cosplay, Anime, Event"
                  required
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  leftSection={<IconCategory size={16} />}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="Creator Tag"
                  placeholder="e.g., @otanative"
                  value={formData.creatorTag}
                  onChange={(e) =>
                    setFormData({ ...formData, creatorTag: e.target.value })
                  }
                  leftSection={<IconUserCheck size={16} />}
                />
              </Grid.Col>
            </Grid>

            {/* OPTIONAL CAPTION */}
            <Textarea
              label="Caption (Optional)"
              description="No caption is scraped for manual uploads — leave blank for none."
              placeholder="Write a caption for the app feed..."
              minRows={3}
              value={formData.customCaption}
              onChange={(e) =>
                setFormData({ ...formData, customCaption: e.target.value })
              }
            />

            <Button
              type="submit"
              size="lg"
              mt="md"
              leftSection={<IconUpload size={20} />}
              className="btn-ota-primary"
            >
              Upload & Publish to App
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}
